import { useLocation, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  ONBOARDING_USER_STATUS,
  USER_IN_REVIEW,
  USER_IS_WAITLISTED,
  ONBOARDING_SUPPORT_EMAIL_ID,
} from "../utils/constants";
import {
  isAdminUser,
  getStoredUserType,
  isAdminUserType,
} from "../utils/userTypeUtils";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import "../styles/protected-route.css";
import { setUserPropertiesGa } from "../utils/gaUtils";
import TrackingButton from "./Tracking/TrackingButton";
import colors from "../config/colors";

export const GuestRoute = () => {
  const { emailUserAuthToken, adminAuthToken } = useAuth();
  if (emailUserAuthToken || adminAuthToken) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
};

export const RegisterRoute = () => {
  const { user, isUserDataLoading, emailUserAuthToken, adminAuthToken } =
    useAuth();
  const isAdmin = isAdminUser(user);
  const status = user?.data?.status;
  const isRegisterFlowUser =
    !isAdmin &&
    (status === USER_IN_REVIEW ||
      status === USER_IS_WAITLISTED ||
      status === ONBOARDING_USER_STATUS);
  const isAuthenticated = emailUserAuthToken || adminAuthToken;

  // Wait for the user: redirecting on a not-yet-loaded user drops /register/status's
  // justSubmitted state, so post-signup review polling never starts.
  if (isAuthenticated && isUserDataLoading) {
    return null;
  }

  // Allow guests (step 1 email guard) and users still in the register/review funnel.
  if (!isAuthenticated || isRegisterFlowUser) {
    return <Outlet />;
  }
  return <Navigate to="/" replace />;
};

export default function ProtectedRoute() {
  const {
    user,
    isUserDataLoading,
    isUserDataError,
    userError,
    emailUserAuthToken,
    setEmailUserAuthToken,
    adminAuthToken,
    setAdminAuthToken,
  } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);
  const isInReview =
    !isAdmin &&
    (user?.data?.status === USER_IN_REVIEW ||
      user?.data?.status === USER_IS_WAITLISTED);

  useEffect(() => {
    if (isUserDataError) {
      const status = userError?.response?.status;
      if ([401, 403].includes(status)) {
        const userType = getStoredUserType();
        [localStorage, sessionStorage].forEach((s) =>
          s.removeItem("authToken"),
        );
        [localStorage, sessionStorage].forEach((s) => s.removeItem("Token"));
        [localStorage, sessionStorage].forEach((s) =>
          s.removeItem("user_type"),
        );
        // [localStorage, sessionStorage].forEach(s => s.removeItem('user_status'));
        setEmailUserAuthToken(null);
        setAdminAuthToken(null);
        queryClient.clear();
        queryClient.removeQueries({ queryKey: ["currentUser"] });
        setUserPropertiesGa({
          emailUserId: null,
          onboardingApplicationId: null,
          brandId: null,
        });
        if (isAdminUserType(userType)) {
          navigate("/brand-admin-login", {
            state: { from: location },
            replace: true,
          });
        } else {
          navigate("/login", { state: { from: location }, replace: true });
        }
      }
    }
  }, [
    isUserDataError,
    userError,
    setEmailUserAuthToken,
    setAdminAuthToken,
    navigate,
    location,
    queryClient,
  ]);

  if (!emailUserAuthToken && !adminAuthToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (isInReview) {
    return (
      <Navigate to="/register/status" state={{ from: location }} replace />
    );
  }

  if (isUserDataError) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 3,
          bgcolor: colors.white,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 160, color: "#EE4D37", mb: 3 }} />
        <Typography
          sx={{
            fontSize: { xs: 26, sm: 30 },
            fontWeight: 700,
            color: "#0D0D0D",
          }}
        >
          Something Went Wrong
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 18 },
            color: "#8A8A8A",
            mt: 1.5,
            maxWidth: 380,
          }}
        >
          Our best minds are working on this.
          <br />
          Please try again.
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: "12px",
            mt: 4,
          }}
        >
          <TrackingButton
            padding="10px 24px"
            fontSize="14px"
            onClick={() => queryClient.refetchQueries(["currentUser"])}
          >
            Try Again
          </TrackingButton>
          <TrackingButton
            variant="secondary"
            padding="10px 24px"
            fontSize="14px"
            onClick={() =>
              window.open(`mailto:${ONBOARDING_SUPPORT_EMAIL_ID}`, "_self")
            }
          >
            Contact Support
          </TrackingButton>
        </Box>
      </Box>
    );
  }

  if (isUserDataLoading) {
    return (
      <Box className="loading-container">
        <Box className="loading-text">
          <p
            style={{
              fontSize: "1.25rem",
              fontWeight: 600,
              color: "#ea580c",
              margin: 0,
            }}
          >
            Securing your connection
          </p>
          <Box display="flex" gap={0.25}>
            <Box className="bounce-dot" />
            <Box className="bounce-dot delay-100" />
            <Box className="bounce-dot delay-200" />
            <Box className="bounce-dot delay-300" />
          </Box>
        </Box>

        <Box className="watermark-text">
          Protected by <span className="wishlink-brand">Wishlink</span>
        </Box>
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <Outlet />;
}
